// src/api/cartApi.js
import axios from "../api/axios";

export const CART_CHANGED_EVENT = "wayroot:cart-changed";

function emitCartChanged(source) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent(CART_CHANGED_EVENT, {
      detail: { source, ts: Date.now() },
    })
  );
}

function extractApiMessage(error, fallback) {
  const msg =
    error?.response?.data?.message ??
    error?.response?.data?.error ??
    error?.response?.data;
  return typeof msg === "string" && msg.trim() ? msg.trim() : fallback;
}

/**
 * Preferred endpoints (new):
 *  GET  /cart/user/id/{userId}
 *  POST /cart/id/{userId}/add
 *  POST /cart/id/{userId}/remove
 *
 * Legacy endpoints:
 *  GET  /cart/{identifier}                  (identifier = email OR phone)
 *  POST /cart/{identifier}/add
 *  POST /cart/{identifier}/remove
 */

function asPositiveInt(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
}

function normalizeIdentifier(identifier) {
  if (identifier == null) return null;
  const s = String(identifier).trim();
  if (!s) return null;

  // email
  if (s.includes("@")) return s;

  // phone: keep last 10 digits
  let p = s.replace(/\D/g, "");
  if (p.length > 10) p = p.slice(-10);
  return /^\d{10}$/.test(p) ? p : s;
}

/**
 * Accepts:
 *  - { userId, phoneNumber, email }
 *  - { id, phoneNumber, email } (common from /current-user)
 *  - { identifier }
 *  - string identifier
 */
function buildUserKey(input) {
  if (typeof input === "string") {
    return { userId: null, identifier: normalizeIdentifier(input) };
  }

  const userId = asPositiveInt(input?.userId) ?? asPositiveInt(input?.id) ?? null;

  const identifier =
    normalizeIdentifier(input?.identifier) ||
    normalizeIdentifier(input?.phoneNumber) ||
    normalizeIdentifier(input?.email) ||
    null;

  return { userId, identifier };
}

function getStoredUserKey() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return { userId: null, identifier: null };
    const u = JSON.parse(raw);
    return buildUserKey(u);
  } catch {
    return { userId: null, identifier: null };
  }
}

/** 🔥 NEW: If user missing in storage but token exists, resolve key from /current-user */
async function resolveKeyFromTokenIfPossible() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return { userId: null, identifier: null };

    // axios default auth header should already be set by your UserContext boot/applyToken.
    // But in case this file is used early, we can set header from storage (safe).
    if (!axios.defaults.headers.common.Authorization) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }

    const { data } = await axios.get("/api/users/current-user");
    const k = buildUserKey(data);

    // store it so future calls don't need to hit /current-user
    if (k.userId || k.identifier) {
      const existingRaw = localStorage.getItem("user");
      let existing = null;
      try {
        existing = existingRaw ? JSON.parse(existingRaw) : null;
      } catch {}

      const merged = { ...(existing || {}), ...(data || {}) };
      localStorage.setItem("user", JSON.stringify(merged));
      return k;
    }
  } catch {
    // ignore
  }
  return { userId: null, identifier: null };
}

/** throws AUTH_REQUIRED if cannot resolve user key */
async function assertKeyAsync(keyLike) {
  const k = buildUserKey(keyLike);
  if (k.userId || k.identifier) return k;

  const stored = getStoredUserKey();
  if (stored.userId || stored.identifier) return stored;

  const fromToken = await resolveKeyFromTokenIfPossible();
  if (fromToken.userId || fromToken.identifier) return fromToken;

  const err = new Error("Missing user identifier (need userId or phone/email identifier)");
  err.code = "AUTH_REQUIRED";
  throw err;
}

/* ----------------------- legacy endpoints ----------------------- */

async function legacyGetCart(identifier) {
  const { data } = await axios.get(`/cart/${encodeURIComponent(identifier)}`);
  return data;
}

async function legacyAdd(identifier, cartItemDto) {
  try {
    const { data, status } = await axios.post(
      `/cart/${encodeURIComponent(identifier)}/add`,
      cartItemDto,
      { headers: { "Content-Type": "application/json" } }
    );
    if (status < 200 || status >= 300) throw new Error("Add to cart failed");
    emitCartChanged("add");
    return data;
  } catch (error) {
    throw new Error(extractApiMessage(error, "Add to cart failed"));
  }
}

async function legacyRemove(identifier, registrationNumber) {
  const { data } = await axios.post(
    `/cart/${encodeURIComponent(identifier)}/remove`,
    registrationNumber,
    { headers: { "Content-Type": "text/plain" } }
  );
  emitCartChanged("remove");
  return data;
}

async function legacyRemoveItemById(identifier, cartItemId) {
  const itemIdNum = asPositiveInt(cartItemId);
  if (!itemIdNum) throw new Error("Missing or invalid cartItemId");

  const { data } = await axios.post(
    `/cart/${encodeURIComponent(identifier)}/remove-item/${itemIdNum}`
  );
  emitCartChanged("remove-item");
  return data;
}

async function legacyUpdateQuantity(identifier, cartItemId, quantity) {
  const itemIdNum = asPositiveInt(cartItemId);
  if (!itemIdNum) throw new Error("Missing or invalid cartItemId");

  const { data } = await axios.post(
    `/cart/${encodeURIComponent(identifier)}/item/${itemIdNum}/quantity`,
    { quantity: Math.max(1, Math.floor(Number(quantity) || 1)) },
    { headers: { "Content-Type": "application/json" } }
  );
  emitCartChanged("update-quantity");
  return data;
}

/* ----------------------- userId endpoints ----------------------- */

export async function getCartByUserId(userId) {
  const idNum = asPositiveInt(userId);
  if (!idNum) throw new Error("Missing or invalid userId");
  const { data } = await axios.get(`/cart/user/id/${idNum}`);
  return data;
}

export async function addToCartByUserId(userId, cartItemDto) {
  const idNum = asPositiveInt(userId);
  if (!idNum) throw new Error("Missing or invalid userId");

  try {
    const { data, status } = await axios.post(`/cart/id/${idNum}/add`, cartItemDto, {
      headers: { "Content-Type": "application/json" },
    });

    if (status < 200 || status >= 300) throw new Error("Add to cart failed");
    emitCartChanged("add");
    return data;
  } catch (error) {
    throw new Error(extractApiMessage(error, "Add to cart failed"));
  }
}

export async function removeFromCartByUserId(userId, registrationNumber) {
  const idNum = asPositiveInt(userId);
  if (!idNum) throw new Error("Missing or invalid userId");
  if (!registrationNumber) throw new Error("Missing registration number");

  const { data } = await axios.post(
    `/cart/id/${idNum}/remove`,
    registrationNumber,
    { headers: { "Content-Type": "text/plain" } }
  );

  emitCartChanged("remove");
  return data;
}

export async function removeCartItemByIdByUserId(userId, cartItemId) {
  const idNum = asPositiveInt(userId);
  const itemIdNum = asPositiveInt(cartItemId);
  if (!idNum) throw new Error("Missing or invalid userId");
  if (!itemIdNum) throw new Error("Missing or invalid cartItemId");

  const { data } = await axios.post(`/cart/id/${idNum}/remove-item/${itemIdNum}`);
  emitCartChanged("remove-item");
  return data;
}

export async function updateCartItemQuantityByUserId(userId, cartItemId, quantity) {
  const idNum = asPositiveInt(userId);
  const itemIdNum = asPositiveInt(cartItemId);
  if (!idNum) throw new Error("Missing or invalid userId");
  if (!itemIdNum) throw new Error("Missing or invalid cartItemId");

  const { data } = await axios.post(
    `/cart/id/${idNum}/item/${itemIdNum}/quantity`,
    { quantity: Math.max(1, Math.floor(Number(quantity) || 1)) },
    { headers: { "Content-Type": "application/json" } }
  );

  emitCartChanged("update-quantity");
  return data;
}

/* ----------------------- unified (recommended) ----------------------- */

export async function getCartForUser(keyLike) {
  const k = await assertKeyAsync(keyLike);
  if (k.userId) return getCartByUserId(k.userId);
  return legacyGetCart(k.identifier);
}

export async function addToCartForUser(keyLike, cartItemDto) {
  const k = await assertKeyAsync(keyLike);
  if (k.userId) return addToCartByUserId(k.userId, cartItemDto);
  return legacyAdd(k.identifier, cartItemDto);
}

export async function removeFromCartForUser(keyLike, registrationNumber) {
  const k = await assertKeyAsync(keyLike);
  if (k.userId) return removeFromCartByUserId(k.userId, registrationNumber);
  return legacyRemove(k.identifier, registrationNumber);
}

export async function removeCartItemByIdForUser(keyLike, cartItemId) {
  const k = await assertKeyAsync(keyLike);
  if (k.userId) return removeCartItemByIdByUserId(k.userId, cartItemId);
  return legacyRemoveItemById(k.identifier, cartItemId);
}

export async function updateCartItemQuantityForUser(keyLike, cartItemId, quantity) {
  const k = await assertKeyAsync(keyLike);
  if (k.userId) return updateCartItemQuantityByUserId(k.userId, cartItemId, quantity);
  return legacyUpdateQuantity(k.identifier, cartItemId, quantity);
}

/* ----------------------- convenience helpers ----------------------- */

export async function addToCartForCurrentUser(cartItemDto) {
  return addToCartForUser(null, cartItemDto);
}

export async function getCartForCurrentUser() {
  return getCartForUser(null);
}

export async function removeFromCartForCurrentUser(regNo) {
  return removeFromCartForUser(null, regNo);
}

export async function removeCartItemByIdForCurrentUser(cartItemId) {
  return removeCartItemByIdForUser(null, cartItemId);
}

export async function updateCartItemQuantityForCurrentUser(cartItemId, quantity) {
  return updateCartItemQuantityForUser(null, cartItemId, quantity);
}
